import { REASONING_EFFORTS, ReasoningEffort } from "@config";
import { ArrowDropDown } from "@mui/icons-material";
import { Button, Menu, MenuItem, Typography } from "@mui/material";
import { useState } from "react";

interface ReasoningEffortSelectorProps {
  selectedReasoningEffort: ReasoningEffort;
  onReasoningEffortSelect: (reasoningEffort: ReasoningEffort) => void;
  supportedReasoningEfforts?: readonly ReasoningEffort[];
}

const REASONING_EFFORT_OPTIONS = [
  { value: "none" as const, label: "None" },
  { value: "low" as const, label: "Low" },
  { value: "medium" as const, label: "Medium" },
  { value: "high" as const, label: "High" },
  { value: "xhigh" as const, label: "X High" },
  { value: "max" as const, label: "Max" },
];

export function ReasoningEffortSelector({
  selectedReasoningEffort,
  onReasoningEffortSelect,
  supportedReasoningEfforts,
}: ReasoningEffortSelectorProps) {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleReasoningEffortSelect = (reasoningEffort: ReasoningEffort) => {
    onReasoningEffortSelect(reasoningEffort);
    handleClose();
  };

  const selectedLabel = REASONING_EFFORT_OPTIONS.find(
    option => option.value === selectedReasoningEffort
  )?.label || "Medium";

  return (
    <>
      <Button
        variant="text"
        size="small"
        onClick={handleClick}
        endIcon={<ArrowDropDown />}
        sx={{
          minWidth: "auto",
          fontSize: "0.75rem",
          padding: "4px 8px",
          borderColor: "rgba(0, 0, 0, 0.23)",
          color: "rgba(0, 0, 0, 0.6)",
          "&:hover": {
            borderColor: "rgba(0, 0, 0, 0.23)",
            backgroundColor: "rgba(0, 0, 0, 0.04)",
          },
        }}
      >
        <Typography variant="caption" sx={{ fontSize: "0.75rem" }}>
          {selectedLabel}
        </Typography>
      </Button>

      <Menu
        id="reasoning-effort-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{
          "aria-labelledby": "reasoning-effort-button",
        }}
        disablePortal
        anchorOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
      >
        {REASONING_EFFORT_OPTIONS.filter((option) =>
          (supportedReasoningEfforts ?? REASONING_EFFORTS).includes(option.value)
        ).map((option) => (
          <MenuItem
            key={option.value}
            onClick={() => handleReasoningEffortSelect(option.value)}
            selected={selectedReasoningEffort === option.value}
          >
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {option.label}
            </Typography>
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}
